// Product recommendations for each color season
// Using Clinique lipsticks + Madison Reed hair colors (Amazon affiliate partners)

export interface ProductRecommendation {
  name: string;
  brand: string;
  category: "lipstick" | "hair";
  description: string;
  amazonUrl: string;
  imageUrl: string;
}

export interface SeasonProducts {
  lipsticks: ProductRecommendation[];
  hairColors: ProductRecommendation[];
  shoppingTips: string[];
}

// Clinique Lipsticks
const clinique = {
  blackHoney: {
    name: "Almost Lipstick - Black Honey",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Universally flattering sheer berry. Adapts to your natural lip color for a 'your lips but better' effect.",
    amazonUrl: "https://amzn.to/4maG2Bi",
    imageUrl: "https://m.media-amazon.com/images/I/610bwNWu-9L._SX522_.jpg",
  },
  pinkHoney: {
    name: "Almost Lipstick - Pink Honey",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Soft rosy pink tinted balm. Sheer, buildable color that enhances natural lip tone.",
    amazonUrl: "https://amzn.to/4ct2HWb",
    imageUrl: "https://m.media-amazon.com/images/I/61f0Nv49ELL._SX522_.jpg",
  },
  barePop: {
    name: "Pop Longwear - Bare Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Neutral nude with 8-hour wear. Clean, natural finish for everyday.",
    amazonUrl: "https://amzn.to/4e7qImV",
    imageUrl: "https://m.media-amazon.com/images/I/71GIg0fAqbL._SX522_.jpg",
  },
  beigePop: {
    name: "Pop Longwear - Beige Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Warm nude beige. Perfect for warm undertones seeking a natural look.",
    amazonUrl: "https://amzn.to/4dsovSX",
    imageUrl: "https://m.media-amazon.com/images/I/71nn6f-mF8L._SX522_.jpg",
  },
  blushPop: {
    name: "Pop Longwear - Blush Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Soft pink with satin finish. Romantic and feminine for cool-toned skin.",
    amazonUrl: "https://amzn.to/4c7Qi8J",
    imageUrl: "https://m.media-amazon.com/images/I/71MDMwI59KL._SX522_.jpg",
  },
  sugarPop: {
    name: "Pop Longwear - Sugar Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Bright pink with blue undertones. Fresh and youthful for cool coloring.",
    amazonUrl: "https://amzn.to/4ca9TVZ",
    imageUrl: "https://m.media-amazon.com/images/I/71W89qBS2lL._SX522_.jpg",
  },
  melonPop: {
    name: "Pop Longwear - Melon Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Coral peach shade. Warm and vibrant for spring and warm undertones.",
    amazonUrl: "https://amzn.to/4sm2kl4",
    imageUrl: "https://m.media-amazon.com/images/I/71cNmNZRKAL._SL1500_.jpg",
  },
  poppyPop: {
    name: "Pop Longwear - Poppy Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Warm coral-red. Bold yet wearable for warm coloring.",
    amazonUrl: "https://amzn.to/4ec6fgK",
    imageUrl: "https://m.media-amazon.com/images/I/61xqtRJveqL._SL1500_.jpg",
  },
  cherryPop: {
    name: "Pop Longwear - Cherry Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "True red with slight blue base. Classic red that suits most skin tones.",
    amazonUrl: "https://amzn.to/41it6je",
    imageUrl: "https://m.media-amazon.com/images/I/71til5k50gL._SX522_.jpg",
  },
  plumPop: {
    name: "Pop Longwear - Plum Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Deep berry plum. Rich and sophisticated for cool undertones.",
    amazonUrl: "https://amzn.to/4ct9v6g",
    imageUrl: "https://m.media-amazon.com/images/I/71eqEDmoB6L._SX522_.jpg",
  },
  blackberryPop: {
    name: "Pop Longwear - Blackberry Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Deep cool berry. Dramatic and flattering for winter coloring.",
    amazonUrl: "https://amzn.to/3QmaKLQ",
    imageUrl: "https://m.media-amazon.com/images/I/71qRd6qdmwL._SX522_.jpg",
  },
  cappuccinoPop: {
    name: "Pop Longwear - Cappuccino Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Warm brown nude. Earthy and flattering for autumn coloring.",
    amazonUrl: "https://amzn.to/4e3Uc58",
    imageUrl: "https://m.media-amazon.com/images/I/71OJ8o-S7xL._SX522_.jpg",
  },
  punchPop: {
    name: "Pop Longwear - Punch Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Bright coral. Vibrant and energetic for clear, warm coloring.",
    amazonUrl: "https://amzn.to/4cbc23R",
    imageUrl: "https://m.media-amazon.com/images/I/71EyeC0N4KL._SX522_.jpg",
  },
  rubyPop: {
    name: "Pop Longwear - Ruby Pop",
    brand: "Clinique",
    category: "lipstick" as const,
    description: "Cool-toned red. Classic and elegant for cool undertones.",
    amazonUrl: "https://amzn.to/4caFl6m",
    imageUrl: "https://m.media-amazon.com/images/I/71R7mXwpP8L._SX522_.jpg",
  },
};

// Madison Reed Hair Colors
const madisonReed = {
  miele: {
    name: "Color Reviving Gloss - Miele",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Honey golden tones. Adds warmth and dimension to blonde and light brown hair.",
    amazonUrl: "https://amzn.to/4ci4UCS",
    imageUrl: "https://m.media-amazon.com/images/I/71PHj5DVQ6L._SX679_.jpg",
  },
  prosecco: {
    name: "Color Reviving Gloss - Prosecco",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Champagne golden blonde. Light golden beige tones with warmth.",
    amazonUrl: "https://amzn.to/4bTNlKf",
    imageUrl: "https://m.media-amazon.com/images/I/81aKWuWk30L._SX679_.jpg",
  },
  espresso: {
    name: "Color Reviving Gloss - Espresso",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Cool dark brown. Neutralizes brassiness for rich, cool brunette tones.",
    amazonUrl: "https://amzn.to/48pT8F1",
    imageUrl: "https://m.media-amazon.com/images/I/71yWb47WysL._SX679_.jpg",
  },
  cannella: {
    name: "Color Reviving Gloss - Cannella",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Auburn copper tones. Adds warmth with copper gold highlights.",
    amazonUrl: "https://amzn.to/3PUQQHN",
    imageUrl: "https://m.media-amazon.com/images/I/819PKPJ5l4L._SX679_.jpg",
  },
  barolo: {
    name: "Color Reviving Gloss - Barolo",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Burgundy wine red. Rich coppery red tones for brunette or red hair.",
    amazonUrl: "https://amzn.to/3Om4lQk",
    imageUrl: "https://m.media-amazon.com/images/I/71am+hS98rL._SX679_.jpg",
  },
  crema: {
    name: "Color Reviving Gloss - Crema",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Cool blonde. Neutralizes yellow tones for crisp, cool results.",
    amazonUrl: "https://amzn.to/4mb2ABU",
    imageUrl: "https://m.media-amazon.com/images/I/814xV7jGMmL._SX679_.jpg",
  },
  amaretto: {
    name: "Color Reviving Gloss - Amaretto",
    brand: "Madison Reed",
    category: "hair" as const,
    description: "Warm bronze brown. Burnished gold and caramel tones for dimension.",
    amazonUrl: "https://amzn.to/4mb1eXM",
    imageUrl: "https://m.media-amazon.com/images/I/719L3cwLi-L._SX679_.jpg",
  },
};

export const productRecommendations: Record<string, Record<string, SeasonProducts>> = {
  SPRING: {
    LIGHT: {
      lipsticks: [
        clinique.pinkHoney,
        clinique.barePop,
        clinique.melonPop,
      ],
      hairColors: [
        madisonReed.miele,
        madisonReed.prosecco,
      ],
      shoppingTips: [
        "Look for lipsticks with 'peach', 'coral', or 'apricot' in the name",
        "Avoid anything with 'ash', 'cool', or 'berry' in hair color names",
        "Cream and satin finishes work better than stark mattes for your delicate coloring",
      ],
    },
    TRUE: {
      lipsticks: [
        clinique.melonPop,
        clinique.poppyPop,
        clinique.punchPop,
      ],
      hairColors: [
        madisonReed.miele,
        madisonReed.cannella,
        madisonReed.amaretto,
      ],
      shoppingTips: [
        "You can handle more saturated warm colors than Light Spring",
        "Glossy and satin lipstick finishes enhance your natural radiance",
        "Hair colors with 'copper', 'gold', or 'strawberry' suit you beautifully",
      ],
    },
    BRIGHT: {
      lipsticks: [
        clinique.punchPop,
        clinique.cherryPop,
        clinique.poppyPop,
      ],
      hairColors: [
        madisonReed.cannella,
        madisonReed.miele,
      ],
      shoppingTips: [
        "You can wear the most saturated warm colors of all Springs",
        "Avoid anything 'dusty', 'muted', or 'soft' in product names",
        "Clear, bright shades work better than subtle ones",
      ],
    },
  },
  SUMMER: {
    LIGHT: {
      lipsticks: [
        clinique.pinkHoney,
        clinique.blushPop,
        clinique.barePop,
      ],
      hairColors: [
        madisonReed.crema,
        madisonReed.prosecco,
      ],
      shoppingTips: [
        "Look for 'ash', 'cool', 'mauve', or 'rose' in product names",
        "Avoid anything with 'golden', 'warm', 'coral', or 'peach'",
        "Soft satin finishes complement your delicate coloring better than bold mattes",
      ],
    },
    TRUE: {
      lipsticks: [
        clinique.blushPop,
        clinique.plumPop,
        clinique.pinkHoney,
      ],
      hairColors: [
        madisonReed.espresso,
        madisonReed.crema,
      ],
      shoppingTips: [
        "Your colors should be cool AND muted—not too bright, not too warm",
        "Dusty rose, soft mauve, and muted berry are your core lip shades",
        "Hair colors should have cool undertones without being stark or icy",
      ],
    },
    COOL: {
      lipsticks: [
        clinique.sugarPop,
        clinique.rubyPop,
        clinique.plumPop,
      ],
      hairColors: [
        madisonReed.crema,
        madisonReed.espresso,
      ],
      shoppingTips: [
        "You can handle clearer cool tones than other Summers",
        "Berry, cool pink, and blue-based rose work beautifully",
        "Any hint of warmth in products will look off on you",
      ],
    },
  },
  AUTUMN: {
    SOFT: {
      lipsticks: [
        clinique.beigePop,
        clinique.cappuccinoPop,
        clinique.blackHoney,
      ],
      hairColors: [
        madisonReed.amaretto,
        madisonReed.miele,
      ],
      shoppingTips: [
        "Your colors should be warm but MUTED—never bright or intense",
        "Look for 'dusty', 'soft', 'muted', or 'nude' in product names",
        "Hair colors should have warmth but not look like bright copper or red",
      ],
    },
    TRUE: {
      lipsticks: [
        clinique.cappuccinoPop,
        clinique.poppyPop,
        clinique.cherryPop,
      ],
      hairColors: [
        madisonReed.cannella,
        madisonReed.amaretto,
        madisonReed.barolo,
      ],
      shoppingTips: [
        "You can wear more saturated warm colors than Soft Autumn",
        "Terracotta, burnt orange, and warm chestnut are your power shades",
        "Hair colors with copper, auburn, or gold work beautifully",
      ],
    },
    DEEP: {
      lipsticks: [
        clinique.blackHoney,
        clinique.plumPop,
        clinique.cherryPop,
      ],
      hairColors: [
        madisonReed.barolo,
        madisonReed.espresso,
        madisonReed.amaretto,
      ],
      shoppingTips: [
        "You can handle the richest, deepest warm shades",
        "Burgundy, deep terracotta, and warm chocolate are your power colors",
        "Hair colors can be darker than other Autumns—embrace the depth",
      ],
    },
  },
  WINTER: {
    COOL: {
      lipsticks: [
        clinique.rubyPop,
        clinique.blackberryPop,
        clinique.sugarPop,
      ],
      hairColors: [
        madisonReed.espresso,
        madisonReed.crema,
      ],
      shoppingTips: [
        "Look for 'blue-based', 'cool', 'berry', or 'plum' in lip products",
        "Hair colors with ash tones keep your color cool",
        "Avoid anything with 'warm', 'golden', 'copper', or 'coral'",
      ],
    },
    TRUE: {
      lipsticks: [
        clinique.cherryPop,
        clinique.rubyPop,
        clinique.blackberryPop,
      ],
      hairColors: [
        madisonReed.espresso,
        madisonReed.crema,
      ],
      shoppingTips: [
        "You're built for drama—bold, clear colors are your strength",
        "True red, vivid berry, and stark contrast suit you",
        "Hair can be very dark or very light—you handle contrast well",
      ],
    },
    DEEP: {
      lipsticks: [
        clinique.blackberryPop,
        clinique.plumPop,
        clinique.blackHoney,
      ],
      hairColors: [
        madisonReed.espresso,
        madisonReed.barolo,
      ],
      shoppingTips: [
        "Deep burgundy, wine, and rich plum are your signature shades",
        "You can wear the darkest lip colors of all seasons",
        "Hair should stay deep and cool—embrace your dramatic coloring",
      ],
    },
  },
};
